import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import rolesService from "../../services/rolesService"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { ChevronRight, Save, Pencil, Loader2, ArrowLeft, Check, Shield } from "lucide-react"

function FormRoles() {
  const { t } = useTranslation()
  const [sectionsPermissions, setSectionsPermissions] = useState([])
  const [roleInfo, setRoleInfo] = useState({ name: null, description: null })
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const { id } = useParams()

  const NamingObject = {
    dashboard: "Dashboard",
    users: "Users Management",
    clients: "Clients",
    Leads: "Leads",
    adAccounts: "Accounts List",
    creditsAccounts: "Balance Tracking",
    creditsAccountsHistory: "Balance Records",
    providers: "Providers",
    tickets: "Tickets",
    expenses: "Assets",
    settings: "Settings",
    topups: "Financial Report",
    roles: "Roles",
    statistics: "Statistics",

  }

  // 🔑 Centralized default section builder
  const buildSections = (roleName, existingPermissions = []) => {
    const defaultSections = [
      "dashboard",
      "users",
      "clients",
      "Leads",
      "adAccounts",
      "creditsAccounts",
      "creditsAccountsHistory",
      "providers",
      "tickets",
      "expenses",
      "settings",
      "roles",
      "topups",
      "statistics",
    ]

    // Every section is always shown and editable. Previously sections were
    // hidden based on the role NAME — which meant new roles could never be
    // granted users/roles permissions, and SAVING an existing role silently
    // stripped any section the filter hid (permission loss on every edit).
    const allowedSections = defaultSections;

    const defaultPermissions = allowedSections.map((section) => ({
      section,
      access: { create: false, read: false, update: false, delete: false },
    }));

    // merge with API permissions (for update case)
    return defaultPermissions.map((defaultPerm) => {
      const existing = existingPermissions.find((p) => p.section === defaultPerm.section)
      return existing ? existing : defaultPerm
    })
  }

  // ================= INIT =================
  useEffect(() => {
    // Set page title in navbar
    const event = new CustomEvent("setPageTitle", {
      detail: { title: id ? "Update Role" : "New Role" },
    })
    window.dispatchEvent(event)

    if (id) {
      getRoleToUpdate()
    } else {
      setRoleInfo({ name: null, description: null })
      setSectionsPermissions(buildSections(null))
    }

    return () => {
      const resetEvent = new CustomEvent("setPageTitle", {
        detail: { title: "Roles" },
      })
      window.dispatchEvent(resetEvent)
    }
  }, [id])

  // ================= API =================
  const getRoleToUpdate = async () => {
    try {
      setErrorMessage("")
      setLoading(true)
      const response = await rolesService.get(id)
      const responseData = response.data
      if (responseData && responseData.statusCode === 200) {
        const role = responseData.data
        setRoleInfo({ name: role.name, description: role.description })
        setSectionsPermissions(buildSections(role.name, role.permissions))
      }
      setLoading(false)
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Failed to fetch role")
      setLoading(false)
      console.error("Role fetch failed:", error.response?.data)
    }
  }

  const save = async (e) => {
    e.preventDefault()
    try {
      setErrorMessage("")
      setLoading(true)
      let response = null
      if (id) {
        response = await rolesService.update(id, {
          name: roleInfo.name,
          description: roleInfo.description,
          permissions: sectionsPermissions,
        })
      } else {
        response = await rolesService.create({
          name: roleInfo.name,
          description: roleInfo.description,
          permissions: sectionsPermissions,
        })
      }

      const responseData = response.data
      if (responseData && responseData.statusCode === 200) {
        toast.success(responseData.message)
        if (id) {
          getRoleToUpdate()
        } else {
          setRoleInfo({ name: null, description: null })
          setSectionsPermissions(buildSections(null))
        }
      }
      setLoading(false)
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Failed to save role")
      toast.error(error.response?.data?.message || "Failed to save role")
      setLoading(false)
      console.error("Role operation failed:", error.response?.data)
    }
  }

  // ================= HANDLERS =================
  const handleCheckboxChange = (section, permission) => (e) => {
    setSectionsPermissions((prevPermissions) =>
      prevPermissions.map((perm) =>
        perm.section === section
          ? { ...perm, access: { ...perm.access, [permission]: e.target.checked } }
          : perm
      )
    )
  }

  const handleCheckAllForSection = (section) => (e) => {
    const isChecked = e.target.checked
    setSectionsPermissions((prevPermissions) =>
      prevPermissions.map((perm) =>
        perm.section === section
          ? {
            ...perm,
            access: {
              read: isChecked,
              create: isChecked,
              update: isChecked,
              delete: isChecked,
            },
          }
          : perm
      )
    )
  }

  const areAllPermissionsChecked = (section) => {
    const sectionData = sectionsPermissions.find((perm) => perm.section === section)
    if (!sectionData) return false
    return Object.values(sectionData.access).every((value) => value === true)
  }

  // ================= RENDER =================
  return (
    <div className="p-7 max-w-full mx-auto">
      {/* Header with breadcrumbs */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <nav className="flex mb-2" aria-label="Breadcrumb">
            <ol className="inline-flex items-center space-x-1 md:space-x-2">
              <li className="inline-flex items-center">
                <Link
                  to="/settings/roles"
                  className="inline-flex items-center text-sm font-[300] text-neutral-600 hover:text-neutral-800 dark:text-neutral-400 dark:hover:text-neutral-300"
                >
                  <Shield className="mr-2 h-4 w-4" />
                  {t("Roles")}
                </Link>
              </li>
              <li>
                <div className="flex items-center">
                  <ChevronRight className="h-4 w-4 text-neutral-400" />
                  <span className="ml-1 text-sm font-[300] text-neutral-600 md:ml-2 dark:text-neutral-400">
                    {id ? t("Update") : t("New")} {t("Role")}
                  </span>
                </div>
              </li>
            </ol>
          </nav>
          <h2 className="text-xl font-[400] text-start text-neutral-900 dark:text-white">
            {id ? t("Update") : t("New")} {t("Role")}
          </h2>
          <p className="mt-1 text-sm text-start text-neutral-500 dark:text-neutral-400">
            {t(
              "Here you will be able to create role by entering the name, description and the permissions of that role."
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/settings/roles"
            className="inline-flex items-center px-4 py-2.5 text-sm font-[300] text-neutral-700 bg-white border border-neutral-200 rounded-xl hover:bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/30 dark:text-neutral-300 dark:bg-neutral-800 dark:hover:bg-neutral-700 focus:outline-none focus:ring-1 focus:ring-neutral-500/40"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("Back")}
          </Link>
          <button
            onClick={save}
            disabled={loading}
            className="inline-flex items-center px-4 py-2.5 text-sm font-[300] bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl 
             focus:outline-none focus:ring-1 focus:ring-blue-500/40 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : id ? (
              <Pencil className="mr-2 h-4 w-4" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {id ? t("Update Role") : t("Save Role")}
          </button>
        </div>
      </div>

      {/* Form inputs */}
      <div className="mb-6 p-6 border border-neutral-200 dark:border-neutral-900  rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-950">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <label htmlFor="role-name" className="block text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 mb-1">
              {t("Role Name")}
            </label>
            <input
              type="text"
              id="role-name"
              className="w-full px-4 h-11 text-sm cursor-not-allowed bg-white border border-neutral-200 rounded-xl text-neutral-900 focus:ring-1 focus:ring-blue-500/40 focus:border-blue-500 dark:border-neutral-900 dark:bg-neutral-900/30 dark:bg-neutral-900/30 dark:text-white"
              placeholder={t("Enter role name")}
              value={roleInfo.name || ""}
              onChange={(e) => setRoleInfo({ ...roleInfo, name: e.target.value })}
              disabled={id}
            />
          </div>
          <div className="relative">
            <label
              htmlFor="role-description"
              className="block text-xs text-start font-[300] text-neutral-500 dark:text-neutral-400 mb-1"
            >
              {t("Role Description")}
            </label>
            <input
              type="text"
              id="role-description"
              className="w-full px-4 h-11 text-sm bg-white border border-neutral-200 rounded-xl text-neutral-900 focus:ring-1 focus:ring-blue-500/40 focus:border-blue-500 dark:border-neutral-900 dark:bg-neutral-900/30 dark:bg-neutral-900/30 dark:text-white"
              placeholder={t("Enter role description")}
              value={roleInfo.description || ""}
              onChange={(e) => setRoleInfo({ ...roleInfo, description: e.target.value })}
            />
          </div>
        </div>
      </div>

      {/* Permissions Table */}
      <div className="overflow-x-auto border border-neutral-200   rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-950">
        <div className="p-6 border-b border-neutral-200 dark:border-neutral-900 ">
          <h3 className="text-lg font-[400] text-neutral-900 dark:text-white">{t("Permissions")}</h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {t("Manage what actions this role can perform in different sections of the application.")}
          </p>
        </div>
        <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
          <thead className="bg-neutral-50 h-14 dark:border-neutral-900 dark:bg-neutral-900/30">
            <tr>
              <th className="px-6 py-4 text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                {t("Section Name")}
              </th>
              <th className="px-6 py-4 text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                {t("All")}
              </th>
              <th className="px-6 py-4 text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                {t("Read")}
              </th>
              <th className="px-6 py-4 text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                {t("Create")}
              </th>
              <th className="px-6 py-4 text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                {t("Edit")}
              </th>
              <th className="px-6 py-4 text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                {t("Delete")}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white font-[300] dark:bg-neutral-950 divide-y divide-neutral-200 dark:divide-neutral-800">
            {sectionsPermissions.map((item) => (
              <tr key={item.section} className="hover:bg-neutral-50 text-start dark:hover:bg-neutral-800">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-start font-[300] text-neutral-800 dark:text-white capitalize">
                    {NamingObject[item.section]}
                  </div>
                </td>
                <td className="px-6 py-4 text-start whitespace-nowrap">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={areAllPermissionsChecked(item.section)}
                      onChange={handleCheckAllForSection(item.section)}
                      className="sr-only peer"
                    />
                    <div className="w-5 h-5 border rounded-xl border-neutral-300 dark:border-neutral-600 peer-checked:bg-yellow-500 peer-checked:border-yellow-500 flex items-center justify-center">
                      {areAllPermissionsChecked(item.section) && <Check className="h-3 w-3 text-white" />}
                    </div>
                  </label>
                </td>
                {["read", "create", "update", "delete"].map((permission) => (
                  <td key={permission} className="px-6 py-4 whitespace-nowrap">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={item.access[permission]}
                        onChange={handleCheckboxChange(item.section, permission)}
                        className="sr-only peer"
                      />
                      <div className="w-5 h-5 border rounded-xl border-neutral-300 dark:border-neutral-600 peer-checked:bg-yellow-500 peer-checked:border-yellow-500 flex items-center justify-center">
                        {item.access[permission] && <Check className="h-3 w-3 text-white" />}
                      </div>
                    </label>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Error message */}
      {errorMessage && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl dark:bg-red-900/20 dark:border-red-800">
          <p className="text-sm text-red-600 dark:text-red-400">{errorMessage}</p>
        </div>
      )}
    </div>
  )
}

export default FormRoles
